import { useState, useRef } from "react";
import PropTypes from "prop-types";

const UploadDocument = ({ lbaId, name, email, onUploadSuccess }) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef();
  const API_URL = process.env.REACT_APP_API_URL;
  
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = selectedFiles.map(file => ({
      file,
      progress: 0,
      uploaded: false,
      isUploading: false
    }));

    setFiles(prev => [...prev, ...newFiles]);

    // Small delay to ensure the UI renders before starting uploads
    setTimeout(() => {
      for (const fileObj of newFiles) {
        uploadFile(fileObj);
      }
    }, 100);
  };

  const uploadFile = async (fileObj) => {
    const formData = new FormData();
    formData.append("file", fileObj.file);
    formData.append("lbaId", lbaId);

    // Mark as uploading
    setFiles(prev =>
      prev.map(f =>
        f.file === fileObj.file ? { ...f, isUploading: true, progress: 0 } : f
      )
    );

    try {
      // Using XMLHttpRequest for progress tracking since fetch doesn't support upload progress natively
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (progressEvent) => {
        if (progressEvent.lengthComputable) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setFiles(prev =>
            prev.map(f =>
              f.file === fileObj.file ? { ...f, progress: percent } : f
            )
          );
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          // Ensure full bar is shown for visual feedback
          setFiles(prev =>
            prev.map(f =>
              f.file === fileObj.file ? { ...f, progress: 100 } : f
            )
          );

          // Delay to allow 100% progress bar to be seen, then remove from list
          setTimeout(() => {
            // Remove the uploaded file from the files list
            setFiles(prev => prev.filter(f => f.file !== fileObj.file));
            
            // Call the success callback to refresh the uploaded documents
            if (onUploadSuccess) {
              onUploadSuccess(fileObj.file); // Pass the uploaded File object here
            }
          }, 800);
        } else {
          // Handle upload error
          console.error("Upload failed with status:", xhr.status);
          setFiles(prev =>
            prev.map(f =>
              f.file === fileObj.file ? { ...f, progress: 0, error: true, isUploading: false } : f
            )
          );
        }
      });

      xhr.addEventListener('error', () => {
        console.error("Upload failed");
        setFiles(prev =>
          prev.map(f =>
            f.file === fileObj.file ? { ...f, progress: 0, error: true, isUploading: false } : f
          )
        );
      });

      xhr.open('POST', `${API_URL}/api/official_documents/upload`);
      xhr.send(formData);

    } catch (error) {
      console.error("Upload failed:", error);
      setFiles(prev =>
        prev.map(f =>
          f.file === fileObj.file ? { ...f, progress: 0, error: true, isUploading: false } : f
        )
      );
    }
  };

  const removeFile = (targetFile) => {
    setFiles(prev => prev.filter(f => f.file !== targetFile));
  };

  const retryUpload = (fileObj) => {
    // Reset error state and retry upload
    setFiles(prev =>
      prev.map(f =>
        f.file === fileObj.file ? { ...f, progress: 0, error: false, isUploading: false } : f
      )
    );
    // Small delay before retrying
    setTimeout(() => {
      uploadFile(fileObj);
    }, 100);
  };

  return (
    <div className="w-full relative flex flex-col items-center justify-start gap-4 text-left text-sm text-neutrals-neutral-900 font-open-sans">
      {/* Header */}
      <div className="self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-xl bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-col items-end justify-start p-6 gap-4">
        <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-center justify-start pt-0 px-0 pb-4 relative gap-2">
          <div className="w-[50px] relative rounded-[50%] bg-primary-primary-50 h-[50px] z-[0]" />
          <div className="flex-1 flex flex-col items-start justify-start gap-[3px] z-[1]">
            <div className="self-stretch relative font-semibold">{name || "Debtor name"}</div>
            <div className="self-stretch relative text-slategray-200">{email || "email"}</div>
          </div>
          <div className="w-[18px] absolute !!m-[0 important] top-[8px] left-[19px] text-2xl font-semibold text-black inline-block z-[2]">
            {name?.charAt(0).toUpperCase() || "D"}
          </div>
        </div>

        {/* Upload Box */}
        <div className="self-stretch flex flex-col items-center justify-start text-slategray-100">
          <div
            className="self-stretch rounded-[10px] bg-white border-primary-primary-500 border-solid border-[2px] box-border h-[231px] flex flex-col items-center justify-center p-4 gap-2.5 cursor-pointer"
            onClick={triggerFileInput}
          >
            <input
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
            />
            <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-[10px] bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center p-2.5">
              <div className="w-5 relative h-5">
                <img className="absolute h-full w-full" alt="" src="assets/document-upload.svg" />
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-1.5">
              <div className="relative">
                <span className="font-semibold">Click to upload</span>
                <span className="text-slategray-200"> or drag and drop</span>
              </div>
              <div className="relative text-slategray-200">(Max. File Size: 20 MB)</div>
            </div>
          </div>
        </div>
      </div>

      {/* Files List - Only show files that are currently uploading or failed */}
      {files.map(({ file, progress, error, isUploading }, index) => (
        <div key={index} className="self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-[10px] bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-col items-center justify-center p-4">
          <div className="self-stretch flex flex-row items-start justify-start gap-2.5">
            <div className="w-10 relative h-10">
              <img className="absolute h-full w-full" alt="" src="assets/pdf.svg" />
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-1.5">
              <div className="self-stretch flex flex-row items-start justify-between gap-0">
                <div className="flex-1 flex flex-col items-start justify-start">
                  <div className="self-stretch relative font-semibold">{file.name}</div>
                  <div className="self-stretch relative text-slategray-200">{(file.size / 1024).toFixed(2)} KB</div>
                  {error && (
                    <div className="self-stretch relative text-red-500 text-xs">Upload failed</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {error && (
                    <div 
                      className="w-5 relative h-5 cursor-pointer" 
                      onClick={() => retryUpload({ file, progress, error, isUploading })}
                      title="Retry upload"
                    >
                      <img className="absolute h-full w-full" alt="" src="assets/retry.svg" />
                    </div>
                  )}
                  <div className="w-5 relative h-5 cursor-pointer" onClick={() => removeFile(file)}>
                    <img className="absolute h-full w-full" alt="" src="assets/delete.svg" />
                  </div>
                </div>
              </div>

              {/* Progress bar - show when uploading or has started */}
              {(isUploading || progress > 0) && !error && (
                <div className="self-stretch flex flex-row items-center justify-center gap-2.5">
                  <div className="flex-1 flex flex-col items-start justify-start relative gap-2.5">
                    <div className="self-stretch relative rounded-[100px] bg-primary-primary-50 h-2 z-[0]" />
                    <div
                      className="absolute top-0 left-0 h-full bg-primary-primary-500 transition-all duration-300 ease-out rounded-[100px]"
                      style={{ 
                        width: `${progress}%`,
                        transition: progress === 0 ? 'none' : 'width 0.3s ease-out'
                      }}
                    />
                  </div>
                  <div className="relative font-semibold">{progress}%</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

UploadDocument.propTypes = {
  lbaId: PropTypes.string,
  name: PropTypes.string,
  email: PropTypes.string,
  onUploadSuccess: PropTypes.func,
};

export default UploadDocument;